import React from "react";
import LinkComponent from "../../app/components/link/LinkComponent";
import {ScreenComponent} from "../../app/components/screen/ScreenComponent";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {Paths} from "../../app/components/sidebar/Paths";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";

export default function ITMobileSystemDesignScreen() {
    return (<ScreenComponent>
        <Header/>
        <General/>
        <Prepare/>
    </ScreenComponent>)
}

function Header() {
    return (
        <GradientHeaderComponent title={Paths.interviewTips.nested.mobileSystemDesign.name}/>
    )
}

function General() {
    return (
        <H2SectionComponent header="General System Design Tips & Tricks" removeTopPadding={true}>
            <p>I often see people talking about this one like it's the hardest of the bunch. Everyone is different, so
                it might very well be the most difficult for you, but I think a lot of it is it's just the
                most <i>unknown</i> of the bunch. Coding is easy. You just grind out LeetCode for a while, you're
                probably good. Behavioral - I get it, don't be weird. But system design? What are they
                even <i>looking</i> for.</p>
            <NotPassFail/>
            <Tradeoffs/>
            <Conversation/>
            <Drive/>
        </H2SectionComponent>
    )
}

function NotPassFail() {
    return (
        <H3SectionComponent
            header="Engineers love for things to be in black and white - pass / fail, but system design isn't.">
            <p>Instead, it's a way for the company to figure out what level you are operating at. Are you able to think
                big-picture like a Senior+ candidate, or are you only able to figure out where the basics fit in? Have
                you thought about - and probably <i>seen</i> a lot of edge cases / error states / etc.? The more detail
                you can provide, the better.</p>
        </H3SectionComponent>
    )
}

function Tradeoffs() {
    return (
        <H3SectionComponent header="Talk about the tradeoffs.">
            <p>"There are several ways we can store this data. We could use sharedPreferences, or a SQL database like
                Room, we could also use a NoSQL database. I'm going to go with X because of ABC." The more you can talk
                about your tradeoffs and the real-world experiences you are able to bring alongside that discussion is
                what truly separates senior+ from mid-.</p>
        </H3SectionComponent>
    )
}

function Conversation() {
    return (
        <H3SectionComponent header="It's a conversation, not a lesson.">
            <p>I see so many people make the mistake of going in with a set structure. A general idea of what to talk
                about is great - requirements, high-level, low-level, issues - but, it's important to note that this
                interview is <i>not</i> black or white. They just sit there talking <i>at</i> the interviewer instead of
                working <i>with</i> the interviewer.</p>
        </H3SectionComponent>
    )
}

function Drive() {
    return (
        <H3SectionComponent header="You want to drive, but don't be afraid of using your interviewer as the GPS.">
            <p>Take a moment to stop every couple of minutes and just say something like, "Would you like me to spend
                more time discussing my reasoning for picking X, or would you like me to move on?" Or maybe even
                something like, "I think the most exciting part to take a deep-dive on is A, but is there something else
                you were hoping to cover - maybe B or C?"</p>
        </H3SectionComponent>
    )
}

function Prepare() {
    return (<H2SectionComponent header="How Best to prepare for MOBILE System Design">
        <Greenfield/>
        <StudyBigArchitecture/>
        <WatchMockInterviews/>
        <LearnTradeoffs/>
        <Practice/>
        <TalkTradeoffs/>
        <MockInterviews/>
    </H2SectionComponent>)
}

function Greenfield() {
    return (<H3SectionComponent header="Work on brand new codebases." removeTopPadding={true}>
        <p>Whether it's during your 9-to-5, open-source, side-project, whatever, the more you are thinking about
            architecting a project from the ground-up the better.</p>
    </H3SectionComponent>)
}

function StudyBigArchitecture() {
    return (<H3SectionComponent header="Learn how the big guys are doing things.">
        <p>Meta has a podcast where they talk about how they've built things to focus on Open Source. Google has
            blogs on blogs on blogs. ALL of these companies - including non-FAANG like Airbnb - have blogs about why
            the chose to do something. Or what they did instead once they realized things were not going the way
            they wanted it to. They typically are talking about tradeoffs at <i>scale</i> which is very important for
            these interviews.</p>
    </H3SectionComponent>)
}

function WatchMockInterviews() {
    return (<H3SectionComponent header="Watch mock interviewers.">
        <p>I found <LinkComponent link="https://www.youtube.com/@alementuev/videos">Alex Lementuev's YouTube
            channel</LinkComponent> to be a decent resource. He can be a bit much at times, in my honest opinion,
            but generally he's giving some decent criticism. There truly isn't a lot of mobile-specific system
            design support, but he provides some feedback at the end of the interview that is helpful. It's also
            nice just as a general overview of how these interviews are ran. There's a few others over on YouTube as
            well. I do truly wish there was something better I could recommend, but unlike Backend / Web, there just
            isn't a lot out there it seems.</p>
    </H3SectionComponent>)
}

function LearnTradeoffs() {
    return (<H3SectionComponent header="Learn about tradeoffs.">
        <p>One of the most important things to talk about during a Systems Design interview is tradeoffs. Why are
            you making the choices that you are. A great resource I found for figuring out "trade offs" on mobile
            specifically is a <LinkComponent link="https://github.com/weeeBox/mobile-system-design">repo on
                GitHub</LinkComponent>. For example, it goes into quite some detail about notifications (polling vs
            push vs websocket vs etc.), but also a whole bunch of other things like that. It also goes into some
            detail around what an entry-level response looks like compared to a staff-level response and everything
            in-between.</p>
    </H3SectionComponent>)
}

function Practice() {
    return (<H3SectionComponent header="Practice on your own.">
        <p>As soon as I had learned some of the basics and how to eloquently talk through tradeoffs, I sat down and
            started designing the big apps myself. Or, more specifically, main features of big apps. How would I
            design the Twitter Feed? How would I design a photos app? How would I design Uber? Or Google Drive.
            Etc.</p>
    </H3SectionComponent>)
}

function TalkTradeoffs() {
    return (<H3SectionComponent header="Prepare to be able to talk about tradeoffs.">
        <p>Make sure you feel comfortable <i>talking</i> about several tradeoff points, especially <i>as</i> you
            creating diagrams at the same time. The areas I chose to focus on were: pagination, push notifications,
            CDNs, and different types of backend (websocket for chat vs GraphQL vs HTTPs for example). They don't
            necessarily need to be the same areas, pick what you are most comfortable talking about, have experience
            in, and make sense based on the system you are trying to design.</p>
        <p>It is a skill in itself to be able to talk as you are writing notes about backend, or drawing diagrams of
            the system. Practice actually talking out loud as you are making diagrams. You only have 45-60 minutes.
            You need every second you can get!</p>
    </H3SectionComponent>)
}

function MockInterviews() {
    return (<H3SectionComponent header="Actually do mock interviews.">
        <p>Once you feel comfortable drawing diagrams, and thinking about tradeoffs, practice saying it all out
            loud. Even if it's just to yourself, or maybe a friend. I, personally, chose to use a paid service
            called <LinkComponent link="https://www.hellointerview.com/mock/schedule">HelloInterview</LinkComponent>. I
            enjoyed my experience, but by no means is this the only option.</p>
    </H3SectionComponent>)
}
